import { useDispatch } from 'react-redux'

import type { ModifiedChargeSheetLineItemInput } from '~/api/graphql/generated/types'
import { MIN_QUANTITY } from '~/components/dashboard/soapV2/charges/constants'
import type { OptimisticOverride } from '~/components/dashboard/soapV2/charges/table/hooks/useOptimisticItemUpdates'
import { editChargeSheetItem } from '~/store/duck/clientFinanceData'
import type { InvoiceLineItem } from '~/types'
import { getIsRetailOrderLineItem } from '~/utils/invoiceUtils'

/**
 * Hook for applying discounts to charge sheet items
 * Supports both dollar amount and percentage discounts (mutually exclusive)
 * Only applies to InvoiceLineItem (retail items don't support discounts)
 *
 * Note: discountPercentage is already in decimal format (0.15 for 15%)
 * The ApplyManualDiscountModal handles conversion from percentage display (15%) to decimal (0.15)
 *
 * @param applyOptimistic - Optional callback to apply optimistic updates for immediate UI feedback
 * @returns Handler function for applying discounts
 */
export const useChargeItemDiscount = (
  applyOptimistic?: (itemId: string, changes: OptimisticOverride) => void,
) => {
  const dispatch = useDispatch()

  return (
    item: InvoiceLineItem,
    discountAmount: number,
    quantity: number,
    discountPercentage?: number,
  ) => {
    // Validate quantity before applying discount
    if (Number.isNaN(quantity) || quantity < MIN_QUANTITY) {
      return
    }

    // Validate discount percentage if provided
    if (discountPercentage !== undefined) {
      if (
        Number.isNaN(discountPercentage) ||
        discountPercentage < 0 ||
        discountPercentage > 1
      ) {
        return
      }
    }

    // Validate discount amount if provided
    if (discountAmount !== undefined) {
      if (Number.isNaN(discountAmount) || discountAmount < 0) {
        return
      }
    }

    const isRetailOrder = getIsRetailOrderLineItem(item)

    if (!isRetailOrder) {
      // Apply optimistic update immediately for both quantity and discount
      if (applyOptimistic) {
        applyOptimistic(item.id, { quantity })
      }

      // For invoice line items, use the charge sheet item edit with discount and quantity
      const updateInput: ModifiedChargeSheetLineItemInput = {
        id: item.id,
        expectedModification: item.modificationDate,
        expectedSoapLogModification: item.soapLogModificationDate,
        quantity,
      }

      // Only add the discount field that's being used (mutually exclusive)
      if (discountPercentage !== undefined) {
        // Percentage mode - only include discountPerc
        updateInput.discountPerc = discountPercentage
      } else {
        // Price mode - only include discountAmount
        updateInput.discountAmount = discountAmount
      }

      dispatch(
        editChargeSheetItem({
          updateItemInput: [updateInput],
        }),
      )
    }
    // Note: Retail order line items don't support discountAmount in their update input
    // Discounts for retail items are typically handled through different mechanisms
  }
}
